import { z } from 'zod'

import { nanoidSchema } from '@scalar-org/entities/utility'

import { type Icon, iconSchema, titleSchema } from '@/schema/utility'
import type { Prettify } from '@/types'

import {
  type InternalLinkNavigationEntry,
  internalLinkNavigationEntrySchema,
  type LinkNavigationEntry,
  linkNavigationEntrySchema,
} from './link'
import {
  type InternalOpenApiNavigationEntry,
  internalOpenApiNavigationEntrySchema,
  type OpenApiNavigationEntry,
  openapiNavigationEntrySchema,
} from './openapi'
import {
  type InternalPageNavigationEntry,
  internalPageNavigationEntrySchema,
  type PageNavigationEntry,
  pageNavigationEntrySchema,
} from './pages'

/** Example group navigation entry. */
const _exampleGroup: GroupNavigationEntry = {
  type: 'group',
  title: 'Elements',
  icon: 'phosphor/icon-globe',
  tag: 'Elements',
  children: {
    /** Root folder page. If this path is not provided we will use the first child as the root redirect. */
    '/': {
      type: 'page',
      filepath: 'pages/fire.md',
      title: 'Fire',
    },
    '/earth': {
      title: 'Earth API',
      type: 'openapi',
      filepath: 'api/earth.yaml',
    },
    /** Page nested under the folder */
    '/water/': {
      title: 'Water',
      type: 'group',
      children: {
        '/': {
          type: 'page',
          filepath: 'pages/water.md',
          title: 'Water',
        },
        '/link': {
          type: 'link',
          url: 'https://www.google.com',
          title: 'Google',
        },
      },
    },
  },
}

// ---------------------------------------------------------------------------

type BaseGroupNavigationEntry = {
  type: 'group'
  title: string
  icon?: Icon
  tag?: string
  open?: boolean
}

export type GroupMode = 'nested' | 'flat' | 'folder'

const baseGroupNavigationEntrySchema = z.object({
  type: z.literal('group'),
  title: titleSchema,
  /** Optional icon string (or URL) to show beside the sidebar entry for the page. */
  icon: iconSchema.optional(),
  /** Optional tag to show in the sidebar for the page. */
  tag: z
    .string()
    .optional()
    .describe('Optional tag to show in the sidebar for the page.'),
  /** Optional initial open state of the page in the sidebar.
   * Note: This only applies if the mode is `folder`
   */
  open: z
    .boolean()
    .optional()
    .describe('Optional open state of the page in the sidebar.'),
})

export type GroupNavigationEntryChildren = Record<
  string,
  | GroupNavigationEntry
  | PageNavigationEntry
  | OpenApiNavigationEntry
  | LinkNavigationEntry
>

const childrenSchema = z
  .record(
    z.string(),
    z.union([
      z.lazy(() => groupNavigationEntrySchema),
      pageNavigationEntrySchema,
      openapiNavigationEntrySchema,
      linkNavigationEntrySchema,
    ]),
  )
  .describe(
    'A record of paths to navigation entries. This is used both at the top level and to define child pages of groups. \n' +
      'With nested children the router path will reflect the joined path of the resolved parent and the object key. \n' +
      'Keys should have a leading slash.',
  )

// Three explicit variants — mode owns its own field in each
export type FlatGroupNavigationEntry = Prettify<
  BaseGroupNavigationEntry & {
    mode: 'flat'
    children: GroupNavigationEntryChildren
  }
>

export type NestedGroupNavigationEntry = Prettify<
  BaseGroupNavigationEntry & {
    mode: 'nested'
    children: GroupNavigationEntryChildren
  }
>

export type FolderGroupNavigationEntry = Prettify<
  BaseGroupNavigationEntry & {
    mode?: 'folder'
    /** Optional root page for the folder. If not provided the first child will be used as the root redirect. */
    page?: PageNavigationEntry
    children: GroupNavigationEntryChildren
  }
>

export type GroupNavigationEntry =
  | FlatGroupNavigationEntry
  | NestedGroupNavigationEntry
  | FolderGroupNavigationEntry

export const groupNavigationEntrySchema: z.ZodType<GroupNavigationEntry> =
  z.union([
    baseGroupNavigationEntrySchema.extend({
      mode: z.literal('flat'),
      children: childrenSchema,
    }),
    baseGroupNavigationEntrySchema.extend({
      mode: z.literal('nested'),
      children: childrenSchema,
    }),
    baseGroupNavigationEntrySchema.extend({
      mode: z.literal('folder').optional().default('folder'),
      /** Optional root page for the folder. If not provided the first child will be used as the root redirect. */
      page: pageNavigationEntrySchema.optional(),
      children: childrenSchema,
    }),
  ])

export type NavigationEntry =
  | GroupNavigationEntry
  | PageNavigationEntry
  | OpenApiNavigationEntry
  | LinkNavigationEntry

export const navigationEntrySchema: z.ZodType<NavigationEntry> = z.union([
  groupNavigationEntrySchema,
  pageNavigationEntrySchema,
  openapiNavigationEntrySchema,
  linkNavigationEntrySchema,
])

export type NavigationRecord = Record<string, NavigationEntry>

export const navigationRecordSchema: z.ZodType<NavigationRecord> = z.record(
  z.string(),
  navigationEntrySchema,
)

// ---------------------------------------------------------------------------
// Internal variants

type InternalChildren = Record<
  string,
  | InternalGroupNavigationEntry
  | InternalPageNavigationEntry
  | InternalOpenApiNavigationEntry
  | InternalLinkNavigationEntry
>

const internalChildrenSchema = z
  .record(
    z.string(),
    z.union([
      z.lazy(() => internalGroupNavigationEntrySchema),
      internalPageNavigationEntrySchema,
      internalOpenApiNavigationEntrySchema,
      internalLinkNavigationEntrySchema,
    ]),
  )
  .describe(
    'A record of paths to navigation entries. This is used both at the top level and to define child pages of groups. \n' +
      'With nested children the router path will reflect the joined path of the resolved parent and the object key. \n' +
      'Keys should have a leading slash.',
  )

export type InternalFlatGroupNavigationEntry = Prettify<
  BaseGroupNavigationEntry & {
    uid: string
    mode: 'flat'
    children: InternalChildren
  }
>

export type InternalNestedGroupNavigationEntry = Prettify<
  BaseGroupNavigationEntry & {
    uid: string
    mode: 'nested'
    children: InternalChildren
  }
>

export type InternalFolderGroupNavigationEntry = Prettify<
  BaseGroupNavigationEntry & {
    uid: string
    mode?: 'folder'
    /** Optional root page for the folder. If not provided the first child will be used as the root redirect. */
    page?: InternalPageNavigationEntry
    children: InternalChildren
  }
>

export type InternalGroupNavigationEntry =
  | InternalFlatGroupNavigationEntry
  | InternalNestedGroupNavigationEntry
  | InternalFolderGroupNavigationEntry

export const internalGroupNavigationEntrySchema: z.ZodType<InternalGroupNavigationEntry> =
  z.union([
    baseGroupNavigationEntrySchema.extend({
      uid: nanoidSchema,
      mode: z.literal('flat'),
      children: internalChildrenSchema,
    }),
    baseGroupNavigationEntrySchema.extend({
      uid: nanoidSchema,
      mode: z.literal('nested'),
      children: internalChildrenSchema,
    }),
    baseGroupNavigationEntrySchema.extend({
      uid: nanoidSchema,
      mode: z.literal('folder').optional().default('folder'),
      /** Optional root page for the folder. If not provided the first child will be used as the root redirect. */
      page: internalPageNavigationEntrySchema.optional(),
      children: internalChildrenSchema,
    }),
  ])

export type InternalNavigationEntry =
  | InternalGroupNavigationEntry
  | InternalPageNavigationEntry
  | InternalOpenApiNavigationEntry
  | InternalLinkNavigationEntry

const internalNavigationEntrySchema = z.union([
  internalGroupNavigationEntrySchema,
  internalPageNavigationEntrySchema,
  internalOpenApiNavigationEntrySchema,
  internalLinkNavigationEntrySchema,
]) satisfies z.ZodType<InternalNavigationEntry>

export type InternalNavigationRecord = Record<string, InternalNavigationEntry>

export const internalNavigationRecordSchema: z.ZodType<InternalNavigationRecord> =
  z.record(z.string(), internalNavigationEntrySchema)

// ---------------------------------------------------------------------------
// Root group (top-level tabs, header-links, sidebar-global)

export type RootGroupNavigationEntry = {
  type: 'group'
  title: string
  children: NavigationRecord
}

/** Special instance of group used at the top level (tabs, header-links, sidebar-global). Has a subset of properties. */
export const rootGroupNavigationEntrySchema = z.object({
  type: z.literal('group'),
  title: z.string(),
  /**
   * A record of subpaths to children entries.
   * With nested children the router path will reflect the joined path of the resolved parent and the object key.
   * Keys should have a leading slash.
   */
  children: navigationRecordSchema,
}) satisfies z.ZodType<RootGroupNavigationEntry>

export type InternalRootGroupNavigationEntry = {
  type: 'group'
  title: string
  uid: string
  children: InternalNavigationRecord
}

/** Special instance of group used at the top level (tabs, header-links, sidebar-global). Has a subset of properties. */
export const internalRootGroupNavigationEntrySchema = z.object({
  type: z.literal('group'),
  title: z.string(),
  uid: nanoidSchema,
  /**
   * A record of subpaths to children entries.
   * With nested children the router path will reflect the joined path of the resolved parent and the object key.
   * Keys should have a leading slash.
   */
  children: internalNavigationRecordSchema,
}) satisfies z.ZodType<InternalRootGroupNavigationEntry>
